import React, { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { adminUserActivityAction, adminUserCourseProgressAction, adminUserCourseWeekSectionsAction, adminUserCouponRedemptionsAction, adminUserDevicesAction, adminUserGetAction, adminUserGrowthSnapshotAnswersSummaryAction, adminUserJournalEntriesAction, adminUserNotificationsAction, adminUserOnboardingAnswersSummaryAction, adminUserPurchasesAction, adminUserSavedItemsAction, adminUserSeenHacksAction, adminUserUpdateStatusAction } from "../Action/action";
import { activityDeviceLabel, activityLabel, formatActivityDateTime } from "./user-activity";
import config from "../coreFIles/config";

function formatDate(value) {
  if (!value) return "—";
  const s = String(value).trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
  }
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.toLocaleDateString();
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  }
  return String(value);
}

function moneyLabel(amount, currency) {
  if (amount == null || amount === "") return "—";
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  const cur = currency ? String(currency).toUpperCase() : "";
  return cur ? `${cur} ${n.toFixed(2)}` : n.toFixed(2);
}

function discountRuleLabel(row) {
  if (row?.coupon_discount == null) return "—";
  const n = Number(row.coupon_discount);
  if (!Number.isFinite(n)) return "—";
  return String(row.coupon_type) === "amount" ? moneyLabel(n, row.currency) : `${n}%`;
}

function saleDiscountRuleLabel(sale) {
  if (!sale || sale.discount == null) return "—";
  const n = Number(sale.discount);
  if (!Number.isFinite(n)) return "—";
  return String(sale.discount_type) === "amount" ? moneyLabel(n, sale.currency) : `${n}%`;
}

function couponStatusBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "badge-success";
  if (s === "pending") return "badge-warning";
  if (s === "cancelled" || s === "canceled" || s === "expired") return "badge-danger";
  return "badge-muted";
}

function maskPurchaseToken(token) {
  if (token == null || token === "") return "—";
  const s = String(token);
  if (s.length <= 12) return s;
  return `${s.slice(0, 6)}...${s.slice(-6)}`;
}

function truncateText(value, max = 40) {
  const s = value == null ? "" : String(value).trim();
  if (!s) return "—";
  if (s.length <= max) return s;
  return `${s.slice(0, max).trimEnd()}…`;
}

function titleCase(value) {
  if (value === undefined || value === null) return "—";
  const s = String(value).trim();
  if (!s || s === "—") return "—";
  if (s.includes("@")) return s;
  return s
    .replace(/[_]+/g, " ")
    .split(/(\s+)/)
    .map((part) => {
      if (!part.trim()) return part;
      return part
        .split("-")
        .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word))
        .join("-");
    })
    .join("");
}

function nameFromUser(u) {
  if (!u) return "—";
  if (u.name) return titleCase(u.name);
  if (u.username) return titleCase(u.username);
  if (u.email) return String(u.email);
  return "—";
}

function SmoothCollapse({ open, children }) {
  const wrapRef = useRef(null);
  const innerRef = useRef(null);
  const mountedRef = useRef(false);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const inner = innerRef.current;
    if (!wrap || !inner) return undefined;

    const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    if (!mountedRef.current) {
      mountedRef.current = true;
      wrap.style.transition = prefersReduced ? "none" : "";
      wrap.style.height = open ? "auto" : "0px";
      wrap.style.opacity = open ? "1" : "0";
      wrap.style.pointerEvents = open ? "auto" : "none";
      return undefined;
    }

    if (prefersReduced) {
      wrap.style.transition = "none";
      wrap.style.height = open ? "auto" : "0px";
      wrap.style.opacity = open ? "1" : "0";
      wrap.style.pointerEvents = open ? "auto" : "none";
      return undefined;
    }

    if (open) {
      wrap.style.height = "0px";
      wrap.style.opacity = "0";
      const target = inner.scrollHeight;
      void wrap.offsetHeight;
      wrap.style.pointerEvents = "auto";
      wrap.style.height = `${target}px`;
      wrap.style.opacity = "1";
      const onEnd = (event) => {
        if (event.propertyName !== "height") return;
        wrap.style.height = "auto";
      };
      wrap.addEventListener("transitionend", onEnd);
      return () => wrap.removeEventListener("transitionend", onEnd);
    }

    const currentHeight = wrap.getBoundingClientRect().height || inner.scrollHeight;
    wrap.style.height = `${currentHeight}px`;
    wrap.style.opacity = "1";
    void wrap.offsetHeight;
    wrap.style.pointerEvents = "none";
    wrap.style.height = "0px";
    wrap.style.opacity = "0";
    return undefined;
  }, [open]);

  return (
    <div ref={wrapRef} className="row-expand-anim" aria-hidden={!open}>
      <div ref={innerRef} className="row-expand-anim-inner">
        {children}
      </div>
    </div>
  );
}

function initialsFromName(name) {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const a = parts[0]?.[0] || "U";
  const b = parts.length > 1 ? parts[parts.length - 1]?.[0] : (parts[0]?.[1] || "");
  return `${a}${b}`.toUpperCase();
}

function resolvePublicUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  let origin = "";
  try {
    if (config.apiUrl) origin = new URL(config.apiUrl).origin;
  } catch (e) {
    origin = "";
  }
  if (!origin) origin = "http://localhost:3001";
  if (s.startsWith("/")) return `${origin}${s}`;
  return `${origin}/${s}`;
}

const TAB_PER_PAGE = 10;

function buildPageButtons(page, totalPages, max = 5) {
  const safeTotal = Math.max(1, totalPages);
  const current = Math.min(Math.max(1, page), safeTotal);
  let s = Math.max(1, current - 2);
  let e = Math.min(safeTotal, s + max - 1);
  s = Math.max(1, e - max + 1);
  const arr = [];
  for (let i = s; i <= e; i += 1) arr.push(i);
  return arr;
}

function TabPagination({ label, page, total, perPage, loading, onPageChange }) {
  const totalPages = total ? Math.max(1, Math.ceil(total / perPage)) : 1;
  const start = total && total > 0 ? (page - 1) * perPage + 1 : 0;
  const end = total && total > 0 ? Math.min(page * perPage, total) : 0;
  const pageButtons = buildPageButtons(page, totalPages);
  if (!total) return null;
  return (
    <div className="pagination">
      <span>
        Showing {start}-{end} of {total} {titleCase(label)}
      </span>
      <div className="pagination-pages">
        <button
          type="button"
          onClick={() => onPageChange(1)}
          disabled={page <= 1 || loading}
          title="First page"
          aria-label="First page"
        >
          {"‹‹"}
        </button>
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1 || loading}
          title="Previous page"
          aria-label="Previous page"
        >
          ‹
        </button>
        {pageButtons.map((p) => (
          <button
            key={p}
            className={p === page ? "active" : ""}
            type="button"
            onClick={() => onPageChange(p)}
            disabled={loading}
          >
            {p}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page >= totalPages || loading}
          title="Next page"
          aria-label="Next page"
        >
          ›
        </button>
        <button
          type="button"
          onClick={() => onPageChange(totalPages)}
          disabled={page >= totalPages || loading}
          title="Last page"
          aria-label="Last page"
        >
          {"››"}
        </button>
      </div>
    </div>
  );
}

export default function UserDetail() {
  const { userId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [user, setUser] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const [activeModal, setActiveModal] = useState(null);
  const [detailTab, setDetailTab] = useState("general");
  const [journals, setJournals] = useState([]);
  const [journalsLoading, setJournalsLoading] = useState(false);
  const [journalsError, setJournalsError] = useState("");
  const [journalsPage, setJournalsPage] = useState(1);
  const [journalsTotal, setJournalsTotal] = useState(0);
  const [activeEntry, setActiveEntry] = useState(null);
  const [activeSavedItem, setActiveSavedItem] = useState(null);
  const [activeNotification, setActiveNotification] = useState(null);

  const [savedFilter, setSavedFilter] = useState("all");
  const [savedItems, setSavedItems] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);
  const [savedError, setSavedError] = useState("");
  const [savedPage, setSavedPage] = useState(1);
  const [savedTotal, setSavedTotal] = useState(0);

  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState("");
  const [notificationsPage, setNotificationsPage] = useState(1);
  const [notificationsTotal, setNotificationsTotal] = useState(0);

  const [seenHacks, setSeenHacks] = useState([]);
  const [seenHacksLoading, setSeenHacksLoading] = useState(false);
  const [seenHacksError, setSeenHacksError] = useState("");
  const [seenHacksPage, setSeenHacksPage] = useState(1);
  const [seenHacksTotal, setSeenHacksTotal] = useState(0);

  const [courseProgress, setCourseProgress] = useState([]);
  const [courseProgressLoading, setCourseProgressLoading] = useState(false);
  const [courseProgressError, setCourseProgressError] = useState("");
  const [courseProgressPage, setCourseProgressPage] = useState(1);
  const [courseProgressTotal, setCourseProgressTotal] = useState(0);
  const [userCertificate, setUserCertificate] = useState(null);
  const [activeWeekProgress, setActiveWeekProgress] = useState(null);
  const [weekSectionsLoading, setWeekSectionsLoading] = useState(false);
  const [weekSectionsError, setWeekSectionsError] = useState("");
  const [expandedWeekSectionId, setExpandedWeekSectionId] = useState(null);

  const [purchases, setPurchases] = useState([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [purchasesError, setPurchasesError] = useState("");
  const [purchasesPage, setPurchasesPage] = useState(1);
  const [purchasesTotal, setPurchasesTotal] = useState(0);
  const [activePurchase, setActivePurchase] = useState(null);

  const [couponHistory, setCouponHistory] = useState([]);
  const [couponHistoryLoading, setCouponHistoryLoading] = useState(false);
  const [couponHistoryError, setCouponHistoryError] = useState("");
  const [couponHistoryPage, setCouponHistoryPage] = useState(1);
  const [couponHistoryTotal, setCouponHistoryTotal] = useState(0);
  const [activeCouponRedemption, setActiveCouponRedemption] = useState(null);

  const [activityRows, setActivityRows] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityError, setActivityError] = useState("");
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);

  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState("");

  const [onboardingSummary, setOnboardingSummary] = useState(null);
  const [onboardingSummaryLoading, setOnboardingSummaryLoading] = useState(false);
  const [onboardingSummaryError, setOnboardingSummaryError] = useState("");

  const [growthSnapshotSummary, setGrowthSnapshotSummary] = useState(null);
  const [growthSnapshotSummaryLoading, setGrowthSnapshotSummaryLoading] = useState(false);
  const [growthSnapshotSummaryError, setGrowthSnapshotSummaryError] = useState("");

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        setActiveModal(null);
        setActiveEntry(null);
        setActiveSavedItem(null);
        setActiveNotification(null);
        setActiveWeekProgress(null);
        setActivePurchase(null);
        setActiveCouponRedemption(null);
        setWeekSectionsError("");
        setExpandedWeekSectionId(null);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const res = await adminUserGetAction(userId);
        if (cancelled) return;
        if (!res?.success) {
          setUser(null);
          setError(res?.message || res?.msg || "Failed to load user.");
          return;
        }
        setUser(res.data || null);
      } catch (e) {
        if (cancelled) return;
        setUser(null);
        setError(e?.response?.data?.message || e?.message || "Failed to load user.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    if (!userId || detailTab !== "journals") return;
    let cancelled = false;
    async function loadJournals() {
      setJournalsLoading(true);
      setJournalsError("");
      try {
        const res = await adminUserJournalEntriesAction(userId, {
          page: journalsPage,
          per_page: TAB_PER_PAGE
        });
        if (cancelled) return;
        if (!res?.success) {
          setJournals([]);
          setJournalsTotal(0);
          setJournalsError(res?.message || res?.msg || "Failed to load journal entries.");
          return;
        }
        setJournals(Array.isArray(res.data) ? res.data : []);
        setJournalsTotal(Number(res?.meta?.total || 0));
      } catch (e) {
        if (cancelled) return;
        setJournals([]);
        setJournalsTotal(0);
        setJournalsError(e?.response?.data?.message || e?.message || "Failed to load journal entries.");
      } finally {
        if (!cancelled) setJournalsLoading(false);
      }
    }
    loadJournals();
    return () => {
      cancelled = true;
    };
  }, [userId, detailTab, journalsPage]);

  useEffect(() => {
    if (!userId || detailTab !== "saved") return;
    let cancelled = false;
    async function loadSaved() {
      setSavedLoading(true);
      setSavedError("");
      try {
        const res = await adminUserSavedItemsAction(userId, {
          type: savedFilter || "all",
          page: savedPage,
          per_page: TAB_PER_PAGE
        });
        if (cancelled) return;
        if (!res?.success) {
          setSavedItems([]);
          setSavedTotal(0);
          setSavedError(res?.message || res?.msg || "Failed to load saved items.");
          return;
        }
        setSavedItems(Array.isArray(res.data) ? res.data : []);
        setSavedTotal(Number(res?.meta?.total || 0));
      } catch (e) {
        if (cancelled) return;
        setSavedItems([]);
        setSavedTotal(0);
        setSavedError(e?.response?.data?.message || e?.message || "Failed to load saved items.");
      } finally {
        if (!cancelled) setSavedLoading(false);
      }
    }
    loadSaved();
    return () => {
      cancelled = true;
    };
  }, [userId, detailTab, savedFilter, savedPage]);

  useEffect(() => {
    if (!userId || detailTab !== "notifications") return;
    let cancelled = false;
    async function loadNotifications() {
      setNotificationsLoading(true);
      setNotificationsError("");
      try {
        const res = await adminUserNotificationsAction(userId, {
          page: notificationsPage,
          per_page: TAB_PER_PAGE
        });
        if (cancelled) return;
        if (!res?.success) {
          setNotifications([]);
          setNotificationsTotal(0);
          setNotificationsError(res?.message || res?.msg || "Failed to load notifications.");
          return;
        }
        setNotifications(Array.isArray(res.data) ? res.data : []);
        setNotificationsTotal(Number(res?.meta?.total || 0));
      } catch (e) {
        if (cancelled) return;
        setNotifications([]);
        setNotificationsTotal(0);
        setNotificationsError(e?.response?.data?.message || e?.message || "Failed to load notifications.");
      } finally {
        if (!cancelled) setNotificationsLoading(false);
      }
    }
    loadNotifications();
    return () => {
      cancelled = true;
    };
  }, [userId, detailTab, notificationsPage]);

  useEffect(() => {
    if (!userId || detailTab !== "seen-hacks") return;
    let cancelled = false;
    async function loadSeenHacks() {
      setSeenHacksLoading(true);
      setSeenHacksError("");
      try {
        const res = await adminUserSeenHacksAction(userId, {
          page: seenHacksPage,
          per_page: TAB_PER_PAGE
        });
        if (cancelled) return;
        if (!res?.success) {
          setSeenHacks([]);
          setSeenHacksTotal(0);
          setSeenHacksError(res?.message || res?.msg || "Failed to load seen hacks.");
          return;
        }
        setSeenHacks(Array.isArray(res.data) ? res.data : []);
        setSeenHacksTotal(Number(res?.meta?.total || 0));
      } catch (e) {
        if (cancelled) return;
        setSeenHacks([]);
        setSeenHacksTotal(0);
        setSeenHacksError(e?.response?.data?.message || e?.message || "Failed to load seen hacks.");
      } finally {
        if (!cancelled) setSeenHacksLoading(false);
      }
    }
    loadSeenHacks();
    return () => {
      cancelled = true;
    };
  }, [userId, detailTab, seenHacksPage]);

  useEffect(() => {
    if (!userId || detailTab !== "course-progress") return;
    let cancelled = false;
    async function loadCourseProgress() {
      setCourseProgressLoading(true);
      setCourseProgressError("");
      try {
        const res = await adminUserCourseProgressAction(userId, {
          page: courseProgressPage,
          per_page: TAB_PER_PAGE
        });
        if (cancelled) return;
        if (!res?.success) {
          setCourseProgress([]);
          setCourseProgressTotal(0);
          setUserCertificate(null);
          setCourseProgressError(res?.message || res?.msg || "Failed to load course progress.");
          return;
        }
        setCourseProgress(Array.isArray(res.data) ? res.data : []);
        setCourseProgressTotal(Number(res?.meta?.total || 0));
        setUserCertificate(res.certificate || null);
      } catch (e) {
        if (cancelled) return;
        setCourseProgress([]);
        setCourseProgressTotal(0);
        setUserCertificate(null);
        setCourseProgressError(e?.response?.data?.message || e?.message || "Failed to load course progress.");
      } finally {
        if (!cancelled) setCourseProgressLoading(false);
      }
    }
    loadCourseProgress();
    return () => {
      cancelled = true;
    };
  }, [userId, detailTab, courseProgressPage]);

  useEffect(() => {
    if (!userId || detailTab !== "purchases") return;
    let cancelled = false;
    async function loadPurchases() {
      setPurchasesLoading(true);
      setPurchasesError("");
      try {
        const res = await adminUserPurchasesAction(userId, {
          page: purchasesPage,
          per_page: TAB_PER_PAGE
        });
        if (cancelled) return;
        if (!res?.success) {
          setPurchases([]);
          setPurchasesTotal(0);
          setPurchasesError(res?.message || res?.msg || "Failed to load purchases.");
          return;
        }
        setPurchases(Array.isArray(res.data) ? res.data : []);
        setPurchasesTotal(Number(res?.meta?.total || 0));
      } catch (e) {
        if (cancelled) return;
        setPurchases([]);
        setPurchasesTotal(0);
        setPurchasesError(e?.response?.data?.message || e?.message || "Failed to load purchases.");
      } finally {
        if (!cancelled) setPurchasesLoading(false);
      }
    }
    loadPurchases();
    return () => {
      cancelled = true;
    };
  }, [userId, detailTab, purchasesPage]);

  useEffect(() => {
    if (!userId || detailTab !== "coupon-history") return;
    let cancelled = false;
    async function loadCouponHistory() {
      setCouponHistoryLoading(true);
      setCouponHistoryError("");
      try {
        const res = await adminUserCouponRedemptionsAction(userId, {
          page: couponHistoryPage,
          per_page: TAB_PER_PAGE
        });
        if (cancelled) return;
        if (!res?.success) {
          setCouponHistory([]);
          setCouponHistoryTotal(0);
          setCouponHistoryError(res?.message || res?.msg || "Failed to load coupon history.");
          return;
        }
        setCouponHistory(Array.isArray(res.data) ? res.data : []);
        setCouponHistoryTotal(Number(res?.meta?.total || 0));
      } catch (e) {
        if (cancelled) return;
        setCouponHistory([]);
        setCouponHistoryTotal(0);
        setCouponHistoryError(e?.response?.data?.message || e?.message || "Failed to load coupon history.");
      } finally {
        if (!cancelled) setCouponHistoryLoading(false);
      }
    }
    loadCouponHistory();
    return () => {
      cancelled = true;
    };
  }, [userId, detailTab, couponHistoryPage]);

  useEffect(() => {
    if (!userId || detailTab !== "activity") return;
    let cancelled = false;
    async function loadActivity() {
      setActivityLoading(true);
      setActivityError("");
      try {
        const res = await adminUserActivityAction(userId, {
          page: activityPage,
          per_page: TAB_PER_PAGE
        });
        if (cancelled) return;
        if (!res?.success) {
          setActivityRows([]);
          setActivityTotal(0);
          setActivityError(res?.message || res?.msg || "Failed to load user activity.");
          return;
        }
        setActivityRows(Array.isArray(res.data) ? res.data : []);
        setActivityTotal(Number(res?.meta?.total || 0));
      } catch (e) {
        if (cancelled) return;
        setActivityRows([]);
        setActivityTotal(0);
        setActivityError(e?.response?.data?.message || e?.message || "Failed to load user activity.");
      } finally {
        if (!cancelled) setActivityLoading(false);
      }
    }
    loadActivity();
    return () => {
      cancelled = true;
    };
  }, [userId, detailTab, activityPage]);

  useEffect(() => {
    if (!userId || detailTab !== "devices") return;
    let cancelled = false;
    async function loadDevices() {
      setDevicesLoading(true);
      setDevicesError("");
      try {
        const res = await adminUserDevicesAction(userId);
        if (cancelled) return;
        if (!res?.success) {
          setDevices([]);
          setDevicesError(res?.message || res?.msg || "Failed to load devices.");
          return;
        }
        setDevices(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (cancelled) return;
        setDevices([]);
        setDevicesError(e?.response?.data?.message || e?.message || "Failed to load devices.");
      } finally {
        if (!cancelled) setDevicesLoading(false);
      }
    }
    loadDevices();
    return () => {
      cancelled = true;
    };
  }, [userId, detailTab]);

  useEffect(() => {
    if (!userId || detailTab !== "onboarding-answers") return;
    let cancelled = false;
    async function loadOnboardingSummary() {
      setOnboardingSummaryLoading(true);
      setOnboardingSummaryError("");
      try {
        const res = await adminUserOnboardingAnswersSummaryAction(userId);
        if (cancelled) return;
        if (!res?.success) {
          setOnboardingSummary(null);
          setOnboardingSummaryError(res?.message || res?.msg || "Failed to load onboarding answers.");
          return;
        }
        setOnboardingSummary(res.data || null);
      } catch (e) {
        if (cancelled) return;
        setOnboardingSummary(null);
        setOnboardingSummaryError(e?.response?.data?.message || e?.message || "Failed to load onboarding answers.");
      } finally {
        if (!cancelled) setOnboardingSummaryLoading(false);
      }
    }
    loadOnboardingSummary();
    return () => {
      cancelled = true;
    };
  }, [userId, detailTab]);

  useEffect(() => {
    if (!userId || detailTab !== "growth-snapshot-answers") return;
    let cancelled = false;
    async function loadGrowthSnapshotSummary() {
      setGrowthSnapshotSummaryLoading(true);
      setGrowthSnapshotSummaryError("");
      try {
        const res = await adminUserGrowthSnapshotAnswersSummaryAction(userId);
        if (cancelled) return;
        if (!res?.success) {
          setGrowthSnapshotSummary(null);
          setGrowthSnapshotSummaryError(res?.message || res?.msg || "Failed to load growth snapshot answers.");
          return;
        }
        setGrowthSnapshotSummary(res.data || null);
      } catch (e) {
        if (cancelled) return;
        setGrowthSnapshotSummary(null);
        setGrowthSnapshotSummaryError(
          e?.response?.data?.message || e?.message || "Failed to load growth snapshot answers."
        );
      } finally {
        if (!cancelled) setGrowthSnapshotSummaryLoading(false);
      }
    }
    loadGrowthSnapshotSummary();
    return () => {
      cancelled = true;
    };
  }, [userId, detailTab]);

  const openWeekSections = async (weekRow) => {
    if (!userId || !weekRow?.week_id) return;
    setActiveModal("course-week");
    setActiveWeekProgress({ week: weekRow, sections: [] });
    setExpandedWeekSectionId(null);
    setWeekSectionsLoading(true);
    setWeekSectionsError("");
    try {
      const res = await adminUserCourseWeekSectionsAction(userId, weekRow.week_id);
      if (!res?.success) {
        setWeekSectionsError(res?.message || res?.msg || "Failed to load section progress.");
        return;
      }
      setActiveWeekProgress({
        week: res.data?.week || weekRow,
        sections: Array.isArray(res.data?.sections) ? res.data.sections : []
      });
    } catch (e) {
      setWeekSectionsError(e?.response?.data?.message || e?.message || "Failed to load section progress.");
    } finally {
      setWeekSectionsLoading(false);
    }
  };

  const toggleWeekSectionExpand = (weekSectionId) => {
    setExpandedWeekSectionId((prev) => (prev === weekSectionId ? null : weekSectionId));
  };

  const displayName = useMemo(() => nameFromUser(user), [user]);
  const statusLabel = user?.is_active !== undefined && user?.is_active !== null ? (Number(user.is_active) ? "Active" : "Inactive") : "—";
  const avatarUrl = useMemo(() => resolvePublicUrl(user?.avatar_url), [user?.avatar_url]);

  const children = useMemo(() => (Array.isArray(user?.children) ? user.children : []), [user]);
  const notif = useMemo(
    () => ({
      morning: user?.time_morning || "—",
      afternoon: user?.time_afternoon || "—",
      evening: user?.time_evening || "—"
    }),
    [user]
  );

  const toggleStatus = async () => {
    if (!user?.id) return;
    const current = Number(user?.is_active) ? 1 : 0;
    const next = current ? 0 : 1;
    setStatusUpdating(true);
    setError("");
    try {
      const res = await adminUserUpdateStatusAction(user.id, next);
      if (!res?.success) throw new Error(res?.message || res?.msg || "Failed to update status.");
      setUser((prev) => (prev ? { ...prev, is_active: next } : prev));
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || "Failed to update status.");
    } finally {
      setStatusUpdating(false);
    }
  };

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>User data unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div className="breadcrumb" style={{ marginBottom: 0 }}>
          <Link to="/users">Users</Link>
          <span>›</span>
          <span>{loading ? "Loading…" : displayName}</span>
        </div>
        <Link to="/users" className="btn btn-outline btn-sm">
          ← Back
        </Link>
      </div>

      <div className="card mb-6 user-detail-card">
        <div className="flex items-center justify-between">
          <div className="profile-header">
            <div className="profile-avatar" style={{ overflow: "hidden" }}>
              {avatarUrl ? (
                <img
                  src={avatarUrl}
                  alt="Profile"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                initialsFromName(displayName)
              )}
            </div>
            <div className="profile-info">
              <h2>{loading ? "Loading…" : displayName}</h2>
              <p>
                Registered on {formatDate(user?.created_at)} · {statusLabel}
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Link to={`/users/${userId}/edit`} className="btn btn-primary btn-sm">
              ✏️ Edit User
            </Link>
            <button className="btn btn-danger btn-sm" type="button" onClick={toggleStatus} disabled={loading || statusUpdating || !user?.id}>
              {statusUpdating ? "Updating…" : (Number(user?.is_active) ? "Deactivate User" : "Activate User")}
            </button>
          </div>
        </div>

        <div className="info-grid">
          <div className="info-item">
            <label>Email</label>
            <p>{user?.email || "—"}</p>
          </div>
          <div className="info-item">
            <label>Phone</label>
            <p>{user?.phone || "—"}</p>
          </div>
          <div className="info-item">
            <label>App Version</label>
            <p>{user?.app_version || "—"}</p>
          </div>
          <div className="info-item">
            <label>Launch terms accepted</label>
            <p>
              {user?.launch_terms_accepted_at
                ? `${formatDate(user.launch_terms_accepted_at)}${
                    user?.launch_terms_version ? ` · v${user.launch_terms_version}` : ""
                  }`
                : "Not accepted"}
            </p>
          </div>
          <div className="info-item">
            <label>Relationship to Child</label>
            <p>{titleCase(user?.relationship_to_child)}</p>
          </div>
          <div className="info-item">
            <label>Adult Age</label>
            <p>{user?.adult_age ?? "—"}</p>
          </div>
          <div className="info-item">
            <label>Total Journals</label>
            <p>{user?.total_journals ?? "—"}</p>
          </div>
          <div className="info-item">
            <label>Saved Hacks</label>
            <p>{user?.saved_hacks ?? "—"}</p>
          </div>
        </div>
        <div className="tabs mb-0" role="tablist" aria-label="User activity">
          <button
            type="button"
            role="tab"
            className={`tab${detailTab === "general" ? " active" : ""}`}
            aria-selected={detailTab === "general"}
            onClick={() => setDetailTab("general")}
          >
            General
          </button>
          <button
            type="button"
            role="tab"
            className={`tab${detailTab === "journals" ? " active" : ""}`}
            aria-selected={detailTab === "journals"}
            onClick={() => setDetailTab("journals")}
          >
            Journals
          </button>
          <button
            type="button"
            role="tab"
            className={`tab${detailTab === "saved" ? " active" : ""}`}
            aria-selected={detailTab === "saved"}
            onClick={() => setDetailTab("saved")}
          >
            Saved Items
          </button>
          <button
            type="button"
            role="tab"
            className={`tab${detailTab === "notifications" ? " active" : ""}`}
            aria-selected={detailTab === "notifications"}
            onClick={() => setDetailTab("notifications")}
          >
            Notifications
          </button>
          <button
            type="button"
            role="tab"
            className={`tab${detailTab === "seen-hacks" ? " active" : ""}`}
            aria-selected={detailTab === "seen-hacks"}
            onClick={() => setDetailTab("seen-hacks")}
          >
            Seen Hacks
          </button>
          <button
            type="button"
            role="tab"
            className={`tab${detailTab === "purchases" ? " active" : ""}`}
            aria-selected={detailTab === "purchases"}
            onClick={() => setDetailTab("purchases")}
          >
            Purchases
          </button>
          <button
            type="button"
            role="tab"
            className={`tab${detailTab === "coupon-history" ? " active" : ""}`}
            aria-selected={detailTab === "coupon-history"}
            onClick={() => setDetailTab("coupon-history")}
          >
            Coupon History
          </button>
          <button
            type="button"
            role="tab"
            className={`tab${detailTab === "course-progress" ? " active" : ""}`}
            aria-selected={detailTab === "course-progress"}
            onClick={() => setDetailTab("course-progress")}
          >
            Course Progress
          </button>
          <button
            type="button"
            role="tab"
            className={`tab${detailTab === "onboarding-answers" ? " active" : ""}`}
            aria-selected={detailTab === "onboarding-answers"}
            onClick={() => setDetailTab("onboarding-answers")}
          >
            Onboarding Answers
          </button>
          <button
            type="button"
            role="tab"
            className={`tab${detailTab === "growth-snapshot-answers" ? " active" : ""}`}
            aria-selected={detailTab === "growth-snapshot-answers"}
            onClick={() => setDetailTab("growth-snapshot-answers")}
          >
            Growth Snapshot
          </button>
          <button
            type="button"
            role="tab"
            className={`tab${detailTab === "activity" ? " active" : ""}`}
            aria-selected={detailTab === "activity"}
            onClick={() => setDetailTab("activity")}
          >
            Activity
          </button>
          <button
            type="button"
            role="tab"
            className={`tab${detailTab === "devices" ? " active" : ""}`}
            aria-selected={detailTab === "devices"}
            onClick={() => setDetailTab("devices")}
          >
            Devices
          </button>
        </div>
      </div>
      
      {detailTab === "general" ? (
        <div>
          <div className="card mb-6">
            <div className="card-header">
              <h2>Children Profiles</h2>
            </div>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Age</th>
                    <th>Gender</th>
                    <th>Siblings</th>
                    <th>Enrolled Course</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={5}>
                        <div className="empty-state" style={{ padding: "28px 12px" }}>
                          Loading…
                        </div>
                      </td>
                    </tr>
                  ) : children.length ? (
                    children.map((c) => (
                      <tr key={c.id ?? `${c.name}-${c.age}`}>
                        <td>
                          <strong>{titleCase(c.name)}</strong>
                        </td>
                        <td>{c.age !== undefined && c.age !== null ? `${c.age} years` : "—"}</td>
                        <td>{titleCase(c.gender)}</td>
                        <td>{c.siblings !== undefined && c.siblings !== null ? String(c.siblings) : "—"}</td>
                        <td>—</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5}>
                        <div className="empty-state" style={{ padding: "28px 12px" }}>
                          No children found.
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card mb-6">
            <div className="card-header">
              <h2>Notification Time Settings</h2>
            </div>
            <p className="help-text">The user's preferred notification times configured in the app.</p>
            <div className="time-settings-grid">
              <div className="time-setting-card">
                <div className="time-icon">🌅</div>
                <h4>Morning</h4>
                <div className="time-value">{notif.morning}</div>
              </div>
              <div className="time-setting-card">
                <div className="time-icon">☀️</div>
                <h4>Afternoon</h4>
                <div className="time-value">{notif.afternoon}</div>
              </div>
              <div className="time-setting-card">
                <div className="time-icon">🌙</div>
                <h4>Evening</h4>
                <div className="time-value">{notif.evening}</div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {detailTab === "journals" ? (
        <div>
          <div className="card">
            <div className="card-header">
              <h2>Recent Journal Entries</h2>
            </div>
            <div className="table-wrapper">
            {journalsError ? (
              <div className="empty-state" style={{ padding: "28px 12px" }}>
                {journalsError}
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Preview</th>
                    <th>Date</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {journalsLoading ? (
                    <tr>
                      <td colSpan={4}>
                        <div className="empty-state" style={{ padding: "28px 12px" }}>
                          Loading…
                        </div>
                      </td>
                    </tr>
                  ) : journals.length ? (
                    journals.map((entry) => (
                      <tr key={entry.id}>
                        <td>
                          <span className={`badge ${Number(entry.type) === 2 ? "badge-info" : "badge-success"}`}>
                            {titleCase(entry.type_label || (Number(entry.type) === 2 ? "Audio" : "Text"))}
                          </span>
                          {Number(entry.favourite) === 1 ? (
                            <span className="badge badge-warning" style={{ marginLeft: 6 }}>
                              Favourite
                            </span>
                          ) : null}
                        </td>
                        <td>{entry.preview || "—"}</td>
                        <td>{formatDate(entry.created_at)}</td>
                        <td>
                          <div className="actions-cell">
                            <button
                              type="button"
                              className="action-link"
                              style={{ background: "transparent", border: "none", padding: 0 }}
                              onClick={() => {
                                setActiveEntry(entry);
                                setActiveModal("journal");
                              }}
                            >
                              View
                            </button>
                        
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4}>
                        <div className="empty-state" style={{ padding: "28px 12px" }}>
                          No journal entries found.
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            )}
            </div>
            <TabPagination
              label="entries"
              page={journalsPage}
              total={journalsTotal}
              perPage={TAB_PER_PAGE}
              loading={journalsLoading}
              onPageChange={setJournalsPage}
            />
          </div>
        </div>
      ) : null}

      {detailTab === "saved" ? (
        <div>
          <div className="card">
            <div className="card-header">
              <h2>Saved Items</h2>
              <select
                className="form-control w-130"
                value={savedFilter}
                onChange={(e) => {
                  setSavedFilter(e.target.value);
                  setSavedPage(1);
                }}
                aria-label="Filter saved items by type"
              >
                <option value="all">All</option>
                <option value="hacks">Hacks</option>
                <option value="videos">Videos</option>
                <option value="resources">Resources</option>
              </select>
            </div>
            <div className="table-wrapper">
              {savedError ? (
                <div className="empty-state" style={{ padding: "28px 12px" }}>
                  {savedError}
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Title</th>
                      <th>Details</th>
                      <th>Saved</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {savedLoading ? (
                      <tr>
                        <td colSpan={5}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            Loading…
                          </div>
                        </td>
                      </tr>
                    ) : savedItems.length ? (
                      savedItems.map((item) => {
                        const typeLabel =
                          item.item_type === "hack"
                            ? "Hack"
                            : item.item_type === "video"
                              ? "Video"
                              : "Resource";
                        const badgeClass =
                          item.item_type === "hack"
                            ? "badge-warning"
                            : item.item_type === "video"
                              ? "badge-info"
                              : "badge-success";
                        const details = [item.source, item.meta, item.preview].filter(Boolean).join(" · ") || "—";
                        return (
                          <tr key={item.key || `${item.item_type}-${item.item_id}`}>
                            <td>
                              <span className={`badge ${badgeClass}`}>{typeLabel}</span>
                            </td>
                            <td>
                              <strong>{titleCase(item.title)}</strong>
                            </td>
                            <td>{details}</td>
                            <td>{formatDate(item.saved_at)}</td>
                            <td>
                              <div className="actions-cell">
                                <button
                                  type="button"
                                  className="action-link"
                                  style={{ background: "transparent", border: "none", padding: 0 }}
                                  onClick={() => {
                                    setActiveSavedItem(item);
                                    setActiveModal("saved");
                                  }}
                                >
                                  View
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={5}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            No saved items found.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
            <TabPagination
              label="items"
              page={savedPage}
              total={savedTotal}
              perPage={TAB_PER_PAGE}
              loading={savedLoading}
              onPageChange={setSavedPage}
            />
          </div>
        </div>
      ) : null}

      {detailTab === "notifications" ? (
        <div>
          <div className="card">
            <div className="card-header">
              <h2>Notifications</h2>
            </div>
            <div className="table-wrapper">
              {notificationsError ? (
                <div className="empty-state" style={{ padding: "28px 12px" }}>
                  {notificationsError}
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Title</th>
                      <th>Preview</th>
                      <th>Slot</th>
                      <th>Sent</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {notificationsLoading ? (
                      <tr>
                        <td colSpan={6}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            Loading…
                          </div>
                        </td>
                      </tr>
                    ) : notifications.length ? (
                      notifications.map((n) => (
                        <tr key={n.id}>
                          <td>
                            <span className="badge badge-info">{titleCase(n.type_label)}</span>
                          </td>
                          <td>
                            <strong>{titleCase(n.title)}</strong>
                          </td>
                          <td>{truncateText(n.preview, 40)}</td>
                          <td>{titleCase(n.slot_label)}</td>
                          <td>{formatDate(n.sent_at || n.notification_date || n.created_at)}</td>
                          <td>
                            <div className="actions-cell">
                              <button
                                type="button"
                                className="action-link"
                                style={{ background: "transparent", border: "none", padding: 0 }}
                                onClick={() => {
                                  setActiveNotification(n);
                                  setActiveModal("notification");
                                }}
                              >
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            No notifications found.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
            <TabPagination
              label="notifications"
              page={notificationsPage}
              total={notificationsTotal}
              perPage={TAB_PER_PAGE}
              loading={notificationsLoading}
              onPageChange={setNotificationsPage}
            />
          </div>
        </div>
      ) : null}

      {detailTab === "seen-hacks" ? (
        <div>
          <div className="card">
            <div className="card-header">
              <h2>Seen Hacks</h2>
            </div>
            <div className="table-wrapper">
              {seenHacksError ? (
                <div className="empty-state" style={{ padding: "28px 12px" }}>
                  {seenHacksError}
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Preview</th>
                      <th>Seen</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seenHacksLoading ? (
                      <tr>
                        <td colSpan={5}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            Loading…
                          </div>
                        </td>
                      </tr>
                    ) : seenHacks.length ? (
                      seenHacks.map((item) => (
                        <tr key={item.key || item.item_id}>
                          <td>
                            <strong>{titleCase(item.title)}</strong>
                          </td>
                          <td>{titleCase(item.category)}</td>
                          <td>{item.preview || "—"}</td>
                          <td>{formatDate(item.seen_at)}</td>
                          <td>
                            <div className="actions-cell">
                              <button
                                type="button"
                                className="action-link"
                                style={{ background: "transparent", border: "none", padding: 0 }}
                                onClick={() => {
                                  setActiveSavedItem(item);
                                  setActiveModal("saved");
                                }}
                              >
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={5}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            No seen hacks found.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
            <TabPagination
              label="Seen Hacks"
              page={seenHacksPage}
              total={seenHacksTotal}
              perPage={TAB_PER_PAGE}
              loading={seenHacksLoading}
              onPageChange={setSeenHacksPage}
            />
          </div>
        </div>
      ) : null}

      {detailTab === "course-progress" ? (
        <div>
          
          <div className="card">
            <div className="card-header">
              <h2>Course Progress</h2>
            </div>
            <div className="table-wrapper">
              {courseProgressError ? (
                <div className="empty-state" style={{ padding: "28px 12px" }}>
                  {courseProgressError}
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th>Week</th>
                      <th>Status</th>
                      <th>Unlocked</th>
                      <th>Completed On</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courseProgressLoading ? (
                      <tr>
                        <td colSpan={6}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            Loading…
                          </div>
                        </td>
                      </tr>
                    ) : courseProgress.length ? (
                      courseProgress.map((row) => (
                        <tr key={row.id || row.week_id}>
                          <td>{titleCase(row.course_title)}</td>
                          <td>
                            <strong>{titleCase(row.week_title)}</strong>
                          </td>
                          <td>
                            <span
                              className={`badge ${
                                row.status === "completed" ? "badge-success" : "badge-warning"
                              }`}
                            >
                              {titleCase(row.status_label || row.status)}
                            </span>
                          </td>
                          <td>{formatDate(row.unlocked_at)}</td>
                          <td>{formatDate(row.completed_at)}</td>
                          <td>
                            <div className="actions-cell">
                              <button
                                type="button"
                                className="action-link"
                                style={{ background: "transparent", border: "none", padding: 0 }}
                                onClick={() => openWeekSections(row)}
                              >
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            No course week progress found.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
            <TabPagination
              label="weeks"
              page={courseProgressPage}
              total={courseProgressTotal}
              perPage={TAB_PER_PAGE}
              loading={courseProgressLoading}
              onPageChange={setCourseProgressPage}
            />
          </div>
        </div>
      ) : null}

      {detailTab === "purchases" ? (
        <div>
          <div className="card">
            <div className="card-header">
              <h2>Purchases</h2>
            </div>
            <div className="table-wrapper">
              {purchasesError ? (
                <div className="empty-state" style={{ padding: "28px 12px" }}>
                  {purchasesError}
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Course</th>
                      <th>Amount</th>
                      <th>Provider</th>
                      <th>Status</th>
                      <th>Onboarding</th>
                      <th>Purchased</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {purchasesLoading ? (
                      <tr>
                        <td colSpan={7}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            Loading…
                          </div>
                        </td>
                      </tr>
                    ) : purchases.length ? (
                      purchases.map((p) => (
                        <tr key={p.id}>
                          <td>
                            <strong>{titleCase(p.course_title)}</strong>
                          </td>
                          <td>{p.amount_label || "—"}</td>
                          <td>{titleCase(p.payment_provider)}</td>
                          <td>
                            <span
                              className={`badge ${
                                String(p.status).toLowerCase() === "completed"
                                  ? "badge-success"
                                  : "badge-warning"
                              }`}
                            >
                              {titleCase(p.status)}
                            </span>
                          </td>
                          <td>{titleCase(p.onboarding_status)}</td>
                          <td>{formatDate(p.created_at)}</td>
                          <td>
                            <div className="actions-cell">
                              <button
                                type="button"
                                className="action-link"
                                style={{ background: "transparent", border: "none", padding: 0 }}
                                onClick={() => {
                                  setActivePurchase(p);
                                  setActiveModal("purchase");
                                }}
                              >
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            No purchases found.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
            <TabPagination
              label="purchases"
              page={purchasesPage}
              total={purchasesTotal}
              perPage={TAB_PER_PAGE}
              loading={purchasesLoading}
              onPageChange={setPurchasesPage}
            />
          </div>
        </div>
      ) : null}

      {detailTab === "coupon-history" ? (
        <div>
          <div className="card">
            <div className="card-header">
              <h2>Coupon History</h2>
            </div>
            <div className="table-wrapper">
              {couponHistoryError ? (
                <div className="empty-state" style={{ padding: "28px 12px" }}>
                  {couponHistoryError}
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Course</th>
                      <th>Original</th>
                      <th>Payable</th>
                      <th>Status</th>
                      <th>Applied</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {couponHistoryLoading ? (
                      <tr>
                        <td colSpan={7}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            Loading…
                          </div>
                        </td>
                      </tr>
                    ) : couponHistory.length ? (
                      couponHistory.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <strong>{r.coupon_code || "—"}</strong>
                          </td>
                          <td>{titleCase(r.course_title)}</td>
                          <td>{moneyLabel(r.original_price, r.currency)}</td>
                          <td>{moneyLabel(r.discounted_price, r.currency)}</td>
                          <td>
                            <span className={`badge ${couponStatusBadgeClass(r.status)}`}>
                              {titleCase(r.status)}
                            </span>
                          </td>
                          <td>{formatDateTime(r.created_on)}</td>
                          <td>
                            <div className="actions-cell">
                              <button
                                type="button"
                                className="action-link"
                                style={{ background: "transparent", border: "none", padding: 0 }}
                                onClick={() => {
                                  setActiveCouponRedemption(r);
                                  setActiveModal("coupon-redemption");
                                }}
                              >
                                View
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            No coupon history found.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
            <TabPagination
              label="coupon applications"
              page={couponHistoryPage}
              total={couponHistoryTotal}
              perPage={TAB_PER_PAGE}
              loading={couponHistoryLoading}
              onPageChange={setCouponHistoryPage}
            />
          </div>
        </div>
      ) : null}

      {detailTab === "onboarding-answers" ? (
        <div>
          <div className="card">
            <div className="card-header">
              <h2>Onboarding Question Answers</h2>
            </div>
            {onboardingSummary?.purchase ? (
              <p className="help-text" style={{ marginBottom: 16 }}>
                {[onboardingSummary.purchase.course_title, `Purchase #${onboardingSummary.purchase.id}`]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            ) : null}
            <div className="table-wrapper">
              {onboardingSummaryError ? (
                <div className="empty-state" style={{ padding: "28px 12px" }}>
                  {onboardingSummaryError}
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Answers</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {onboardingSummaryLoading ? (
                      <tr>
                        <td colSpan={4}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            Loading…
                          </div>
                        </td>
                      </tr>
                    ) : Array.isArray(onboardingSummary?.rows) && onboardingSummary.rows.length ? (
                      onboardingSummary.rows.map((row) => (
                        <tr key={row.key}>
                          <td>
                            <strong>{titleCase(row.label)}</strong>
                          </td>
                          <td>{row.answer_count ?? 0}</td>
                          <td>
                            <span className={`badge ${row.has_data ? "badge-success" : "badge-muted"}`}>
                              {row.has_data ? "Available" : "No data"}
                            </span>
                          </td>
                          <td>
                            <div className="actions-cell">
                              <Link
                                to={`/users/${userId}/onboarding-answers/${row.key}`}
                                className="action-link"
                              >
                                View
                              </Link>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            No onboarding answer summary available.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {detailTab === "growth-snapshot-answers" ? (
        <div>
          <div className="card">
            <div className="card-header">
              <h2>Growth Snapshot Question Answers</h2>
            </div>
            {growthSnapshotSummary?.purchase ? (
              <p className="help-text" style={{ marginBottom: 16 }}>
                {[growthSnapshotSummary.purchase.course_title, `Purchase #${growthSnapshotSummary.purchase.id}`]
                  .filter(Boolean)
                  .join(" · ")}
              </p>
            ) : null}
            <div className="table-wrapper">
              {growthSnapshotSummaryError ? (
                <div className="empty-state" style={{ padding: "28px 12px" }}>
                  {growthSnapshotSummaryError}
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Answers</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {growthSnapshotSummaryLoading ? (
                      <tr>
                        <td colSpan={4}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            Loading…
                          </div>
                        </td>
                      </tr>
                    ) : Array.isArray(growthSnapshotSummary?.rows) && growthSnapshotSummary.rows.length ? (
                      growthSnapshotSummary.rows.map((row) => (
                        <tr key={row.key}>
                          <td>
                            <strong>{titleCase(row.label)}</strong>
                          </td>
                          <td>{row.answer_count ?? 0}</td>
                          <td>
                            <span className={`badge ${row.has_data ? "badge-success" : "badge-muted"}`}>
                              {row.has_data ? "Available" : "No data"}
                            </span>
                          </td>
                          <td>
                            <div className="actions-cell">
                              <Link
                                to={`/users/${userId}/growth-snapshot-answers/${row.key}`}
                                className="action-link"
                              >
                                View
                              </Link>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            No growth snapshot answer summary available.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {detailTab === "activity" ? (
        <div>
          <div className="card">
            <div className="card-header">
              <h2>User Activity</h2>
            </div>
            <div className="table-wrapper">
              {activityError ? (
                <div className="empty-state" style={{ padding: "28px 12px" }}>
                  {activityError}
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Activity</th>
                      <th>Key</th>
                      <th>Device</th>
                      <th>When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activityLoading ? (
                      <tr>
                        <td colSpan={4}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            Loading…
                          </div>
                        </td>
                      </tr>
                    ) : activityRows.length ? (
                      activityRows.map((r) => (
                        <tr key={r.id}>
                          <td>
                            <strong>{activityLabel(r)}</strong>
                          </td>
                          <td>{r.activity || "—"}</td>
                          <td>{activityDeviceLabel(r)}</td>
                          <td>{formatActivityDateTime(r.created_at)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            No activity found.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
            <TabPagination
              label="activities"
              page={activityPage}
              total={activityTotal}
              perPage={TAB_PER_PAGE}
              loading={activityLoading}
              onPageChange={setActivityPage}
            />
          </div>
        </div>
      ) : null}

      {detailTab === "devices" ? (
        <div>
          <div className="card">
            <div className="card-header">
              <h2>Active Devices</h2>
            </div>
            <div className="table-wrapper">
              {devicesError ? (
                <div className="empty-state" style={{ padding: "28px 12px" }}>
                  {devicesError}
                </div>
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Device</th>
                      <th>Type</th>
                      <th>OS</th>
                      <th>Push</th>
                      <th>Sessions</th>
                      <th>Last seen</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {devicesLoading ? (
                      <tr>
                        <td colSpan={7}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            Loading…
                          </div>
                        </td>
                      </tr>
                    ) : devices.length ? (
                      devices.map((r, index) => (
                        <tr key={r.device_id != null ? r.device_id : `device-${index}`}>
                          <td>
                            <strong>{activityDeviceLabel(r)}</strong>
                          </td>
                          <td>{r.device_type || "—"}</td>
                          <td>
                            {[r.os_name, r.os_version].filter(Boolean).join(" ") || "—"}
                          </td>
                          <td>{r.has_fcm_token ? "Yes" : "No"}</td>
                          <td>{r.active_session_count != null ? r.active_session_count : "—"}</td>
                          <td>{formatDateTime(r.last_session_at || r.last_seen_at)}</td>
                          <td>{r.until_next_login ? "Until next login" : "Active"}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7}>
                          <div className="empty-state" style={{ padding: "28px 12px" }}>
                            No active devices.
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <div
        className={`modal-overlay ${activeModal ? "active" : ""}`}
        onClick={() => {
          setActiveModal(null);
          setActiveEntry(null);
          setActiveSavedItem(null);
          setActiveNotification(null);
          setActiveWeekProgress(null);
          setActivePurchase(null);
          setActiveCouponRedemption(null);
          setWeekSectionsError("");
          setExpandedWeekSectionId(null);
        }}
      >
        <div
          className={`modal${activeModal === "course-week" ? " modal-xl" : " modal-lg"}`}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-header">
            <h3>
              {activeModal === "saved"
                ? "Saved Item"
                : activeModal === "notification"
                  ? "Notification"
                  : activeModal === "course-week"
                    ? "Week Section Progress"
                    : activeModal === "purchase"
                      ? "Purchase"
                      : activeModal === "coupon-redemption"
                        ? "Applied Coupon"
                        : "Journal Entry"}
            </h3>
            <button
              className="modal-close"
              type="button"
              onClick={() => {
                setActiveModal(null);
                setActiveEntry(null);
                setActiveSavedItem(null);
                setActiveNotification(null);
                setActiveWeekProgress(null);
                setActivePurchase(null);
                setActiveCouponRedemption(null);
                setWeekSectionsError("");
                setExpandedWeekSectionId(null);
              }}
            >
              ✕
            </button>
          </div>

          <div className="modal-body">
            {activeModal === "purchase" ? (
              activePurchase ? (
                <div>
                  <h3 style={{ marginBottom: 12, fontSize: 18 }}>{titleCase(activePurchase.course_title)}</h3>
                  <div className="info-grid" style={{ marginBottom: 0 }}>
                    <div className="info-item">
                      <label>Amount</label>
                      <p>{activePurchase.amount_label || "—"}</p>
                    </div>
                    <div className="info-item">
                      <label>Status</label>
                      <p>{titleCase(activePurchase.status)}</p>
                    </div>
                    <div className="info-item">
                      <label>Provider</label>
                      <p>{titleCase(activePurchase.payment_provider)}</p>
                    </div>
                    <div className="info-item">
                      <label>Provider ID</label>
                      <p style={{ wordBreak: "break-all" }}>{activePurchase.payment_provider_id || "—"}</p>
                    </div>
                    <div className="info-item">
                      <label>Onboarding</label>
                      <p>
                        {titleCase(activePurchase.onboarding_status)}
                        {activePurchase.onboarding_step != null ? ` (step ${activePurchase.onboarding_step})` : ""}
                      </p>
                    </div>
                    <div className="info-item">
                      <label>Onboarding completed</label>
                      <p>{formatDate(activePurchase.onboarding_completed_at)}</p>
                    </div>
                    <div className="info-item">
                      <label>Purchased</label>
                      <p>{formatDate(activePurchase.created_at)}</p>
                    </div>
                    <div className="info-item">
                      <label>Purchase ID</label>
                      <p>{activePurchase.id ?? "—"}</p>
                    </div>
                  </div>
                  {activePurchase.coupon ? (
                    <div style={{ marginTop: 20 }}>
                      <h4 style={{ margin: "0 0 12px", fontSize: 15 }}>Applied Coupon</h4>
                      <div className="info-grid" style={{ marginBottom: 0 }}>
                        <div className="info-item">
                          <label>Code</label>
                          <p>{activePurchase.coupon.coupon_code || "—"}</p>
                        </div>
                        <div className="info-item">
                          <label>Name</label>
                          <p>{activePurchase.coupon.coupon_name || "—"}</p>
                        </div>
                        <div className="info-item">
                          <label>Rule</label>
                          <p>
                            {titleCase(activePurchase.coupon.coupon_type)} ·{" "}
                            {discountRuleLabel(activePurchase.coupon)}
                          </p>
                        </div>
                        <div className="info-item">
                          <label>Status</label>
                          <p>
                            <span className={`badge ${couponStatusBadgeClass(activePurchase.coupon.status)}`}>
                              {titleCase(activePurchase.coupon.status)}
                            </span>
                          </p>
                        </div>
                        <div className="info-item">
                          <label>Original Price</label>
                          <p>
                            {moneyLabel(
                              activePurchase.coupon.original_price,
                              activePurchase.coupon.currency
                            )}
                          </p>
                        </div>
                        <div className="info-item">
                          <label>Discount Amount</label>
                          <p>
                            {moneyLabel(
                              activePurchase.coupon.discount_amount,
                              activePurchase.coupon.currency
                            )}
                          </p>
                        </div>
                        <div className="info-item">
                          <label>Payable / Discounted</label>
                          <p>
                            {moneyLabel(
                              activePurchase.coupon.discounted_price,
                              activePurchase.coupon.currency
                            )}
                          </p>
                        </div>
                        <div className="info-item">
                          <label>Applied On</label>
                          <p>{formatDateTime(activePurchase.coupon.created_on)}</p>
                        </div>
                      </div>
                    </div>
                  ) : null}
                  {activePurchase.sale ? (
                    <div style={{ marginTop: 20 }}>
                      <h4 style={{ margin: "0 0 12px", fontSize: 15 }}>Applied Sale</h4>
                      <div className="info-grid" style={{ marginBottom: 0 }}>
                        <div className="info-item">
                          <label>Name</label>
                          <p>{activePurchase.sale.sale_name || "—"}</p>
                        </div>
                        <div className="info-item">
                          <label>Rule</label>
                          <p>
                            {titleCase(activePurchase.sale.discount_type)} ·{" "}
                            {saleDiscountRuleLabel(activePurchase.sale)}
                          </p>
                        </div>
                        <div className="info-item">
                          <label>Original Price</label>
                          <p>
                            {moneyLabel(
                              activePurchase.sale.original_price,
                              activePurchase.sale.currency
                            )}
                          </p>
                        </div>
                        <div className="info-item">
                          <label>Discount Amount</label>
                          <p>
                            {moneyLabel(
                              activePurchase.sale.discount_amount,
                              activePurchase.sale.currency
                            )}
                          </p>
                        </div>
                        <div className="info-item">
                          <label>Payable / Discounted</label>
                          <p>
                            {moneyLabel(
                              activePurchase.sale.discounted_price,
                              activePurchase.sale.currency
                            )}
                          </p>
                        </div>
                        <div className="info-item">
                          <label>Applied On</label>
                          <p>{formatDateTime(activePurchase.sale.created_on)}</p>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="empty-state" style={{ padding: "28px 12px" }}>
                  No purchase selected.
                </div>
              )
            ) : activeModal === "coupon-redemption" ? (
              activeCouponRedemption ? (
                <div className="info-grid" style={{ marginBottom: 0 }}>
                  <div className="info-item">
                    <label>Status</label>
                    <p>
                      <span className={`badge ${couponStatusBadgeClass(activeCouponRedemption.status)}`}>
                        {titleCase(activeCouponRedemption.status)}
                      </span>
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Coupon Code</label>
                    <p>{activeCouponRedemption.coupon_code || "—"}</p>
                  </div>
                  <div className="info-item">
                    <label>Coupon Name</label>
                    <p>{activeCouponRedemption.coupon_name || "—"}</p>
                  </div>
                  <div className="info-item">
                    <label>Coupon Rule</label>
                    <p>
                      {titleCase(activeCouponRedemption.coupon_type)} ·{" "}
                      {discountRuleLabel(activeCouponRedemption)}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Course</label>
                    <p>
                      {activeCouponRedemption.course_title ||
                        (activeCouponRedemption.course_id
                          ? `Course #${activeCouponRedemption.course_id}`
                          : "—")}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Original Price</label>
                    <p>
                      {moneyLabel(activeCouponRedemption.original_price, activeCouponRedemption.currency)}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Discount Amount</label>
                    <p>
                      {moneyLabel(activeCouponRedemption.discount_amount, activeCouponRedemption.currency)}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Payable / Discounted</label>
                    <p>
                      {moneyLabel(activeCouponRedemption.discounted_price, activeCouponRedemption.currency)}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Purchase ID</label>
                    <p>{activeCouponRedemption.purchase_id ?? "—"}</p>
                  </div>
                  <div className="info-item">
                    <label>Purchase Token</label>
                    <p style={{ wordBreak: "break-all" }}>
                      {maskPurchaseToken(activeCouponRedemption.purchase_token)}
                    </p>
                  </div>
                  <div className="info-item">
                    <label>Coupon ID</label>
                    <p style={{ wordBreak: "break-all" }}>{activeCouponRedemption.coupon_id || "—"}</p>
                  </div>
                  <div className="info-item">
                    <label>Applied On</label>
                    <p>{formatDateTime(activeCouponRedemption.created_on)}</p>
                  </div>
                  <div className="info-item">
                    <label>Completed On</label>
                    <p>{formatDateTime(activeCouponRedemption.completed_on)}</p>
                  </div>
                </div>
              ) : (
                <div className="empty-state" style={{ padding: "28px 12px" }}>
                  No coupon selected.
                </div>
              )
            ) : activeModal === "course-week" ? (
              activeWeekProgress ? (
                <div>
                  <p className="help-text" style={{ marginBottom: 8 }}>
                    {[
                      titleCase(activeWeekProgress.week?.course_title),
                      titleCase(activeWeekProgress.week?.week_title),
                      titleCase(activeWeekProgress.week?.status_label)
                    ]
                      .filter((v) => v && v !== "—")
                      .join(" · ")}
                  </p>
                  {weekSectionsError ? (
                    <div className="empty-state" style={{ padding: "28px 12px" }}>
                      {weekSectionsError}
                    </div>
                  ) : weekSectionsLoading ? (
                    <div className="empty-state" style={{ padding: "28px 12px" }}>
                      Loading…
                    </div>
                  ) : (
                    <div className="table-wrapper">
                      <table>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Section</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>Watched</th>
                            <th>Ready</th>
                            <th>Completed On</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeWeekProgress.sections?.length ? (
                            activeWeekProgress.sections.map((s) => {
                              const isExpanded = expandedWeekSectionId === s.week_section_id;
                              const media = Array.isArray(s.media) ? s.media : [];
                              return (
                                <Fragment key={s.week_section_id}>
                                  <tr className={isExpanded ? "row-expanded" : undefined}>
                                    <td>{s.position ?? "—"}</td>
                                    <td>
                                      <strong>{titleCase(s.section_title)}</strong>
                                    </td>
                                    <td>{titleCase(s.section_type)}</td>
                                    <td>
                                      {s.is_core_videos ? (
                                        "—"
                                      ) : (
                                        <span
                                          className={`badge ${
                                            s.status === "Completed"
                                              ? "badge-success"
                                              : s.status === "In progress"
                                                ? "badge-warning"
                                                : "badge-muted"
                                          }`}
                                        >
                                          {s.status || "—"}
                                        </span>
                                      )}
                                    </td>
                                    <td>{s.is_core_videos ? "—" : Number(s.is_watched) === 1 ? "Yes" : "No"}</td>
                                    <td>
                                      {s.is_core_videos
                                        ? "—"
                                        : Number(s.ready_for_next_step) === 1
                                          ? "Yes"
                                          : "No"}
                                    </td>
                                    <td>{s.is_core_videos ? "—" : formatDate(s.completed_at)}</td>
                                    <td>
                                      <div className="actions-cell">
                                        <button
                                          type="button"
                                          className="action-link"
                                          onClick={() => toggleWeekSectionExpand(s.week_section_id)}
                                        >
                                          {isExpanded ? "Hide" : "View"}
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                  <tr className={`row-expand-detail${isExpanded ? " is-open" : ""}`}>
                                      <td colSpan={8}>
                                        <SmoothCollapse open={isExpanded}>
                                            <div className="nested-table-wrap">
                                              <h4 style={{ marginBottom: 8, fontSize: 16 }}>
                                                {s.section_type === "videos" ? "Videos" : "Resources"}
                                              </h4>
                                              <table className="nested-table">
                                                <thead>
                                                  <tr>
                                                    <th>#</th>
                                                    <th>Title</th>
                                                    <th>Type</th>
                                                    <th>Viewed</th>
                                                    {s.section_type !== "videos" ? <th>Read</th> : null}
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {media.length ? (
                                                    media.map((m, idx) => (
                                                      <tr key={m.id}>
                                                        <td>{m.position != null ? m.position : idx + 1}</td>
                                                        <td>
                                                          {m.file_url ? (
                                                            <a
                                                              href={resolvePublicUrl(m.file_url)}
                                                              target="_blank"
                                                              rel="noreferrer"
                                                              className="action-link"
                                                            >
                                                              <strong>{m.title ? titleCase(m.title) : "Open"}</strong>
                                                            </a>
                                                          ) : (
                                                            <strong>{titleCase(m.title)}</strong>
                                                          )}
                                                        </td>
                                                        <td>{titleCase(m.file_type)}</td>
                                                        <td>{Number(m.is_viewed) === 1 ? "Yes" : "No"}</td>
                                                        {s.section_type !== "videos" ? <td>{Number(m.is_read) === 1 ? "Yes" : "No"}</td> : null}
                                                      </tr>
                                                    ))
                                                  ) : (
                                                    <tr>
                                                      <td colSpan={6}>
                                                        <div className="empty-state" style={{ padding: "16px 8px" }}>
                                                          No videos or resources in this section.
                                                        </div>
                                                      </td>
                                                    </tr>
                                                  )}
                                                </tbody>
                                              </table>
                                            </div>
                                        </SmoothCollapse>
                                      </td>
                                    </tr>
                                </Fragment>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={8}>
                                <div className="empty-state" style={{ padding: "28px 12px" }}>
                                  No sections found for this week.
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <div className="empty-state" style={{ padding: "28px 12px" }}>
                  No week selected.
                </div>
              )
            ) : activeModal === "notification" ? (
              activeNotification ? (
                <div>
                  <p className="help-text" style={{ marginBottom: 8 }}>
                    {[
                      titleCase(activeNotification.type_label),
                      titleCase(activeNotification.slot_label),
                      formatDate(activeNotification.sent_at || activeNotification.notification_date)
                    ]
                      .filter((v) => v && v !== "—")
                      .join(" · ")}
                  </p>
                  {(activeNotification.week_name || activeNotification.day != null) ? (
                    <p className="help-text" style={{ marginBottom: 8 }}>
                      {[
                        activeNotification.week_name ? `Week: ${activeNotification.week_name}` : null,
                        activeNotification.day != null ? `Day: ${activeNotification.day}` : null
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                  ) : null}
                  <h3 style={{ marginBottom: 12, fontSize: 18 }}>{titleCase(activeNotification.title)}</h3>
                  <div className="journal-content" style={{ whiteSpace: "pre-wrap" }}>
                    {activeNotification.body || "—"}
                  </div>
                </div>
              ) : (
                <div className="empty-state" style={{ padding: "28px 12px" }}>
                  No notification selected.
                </div>
              )
            ) : activeModal === "saved" ? (
              activeSavedItem ? (
                <div>
                  <p className="help-text" style={{ marginBottom: 8 }}>
                    {[
                      activeSavedItem.item_type === "hack"
                        ? "Hack"
                        : activeSavedItem.item_type === "video"
                          ? "Video"
                          : "Resource",
                      activeSavedItem.source,
                      activeSavedItem.meta,
                      formatDate(activeSavedItem.saved_at)
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </p>
                  <h3 style={{ marginBottom: 12, fontSize: 18 }}>{titleCase(activeSavedItem.title)}</h3>
                  {activeSavedItem.thumbnail_url ? (
                    <img
                      src={activeSavedItem.thumbnail_url}
                      alt=""
                      style={{
                        maxWidth: "100%",
                        maxHeight: 180,
                        objectFit: "cover",
                        borderRadius: 8,
                        marginBottom: 12
                      }}
                    />
                  ) : null}
                  <div className="journal-content" style={{ whiteSpace: "pre-wrap", marginBottom: 12 }}>
                    {activeSavedItem.description || activeSavedItem.content || "No description."}
                  </div>
                  {activeSavedItem.item_type === "hack" &&
                  activeSavedItem.content &&
                  activeSavedItem.content !== activeSavedItem.description ? (
                    <div className="journal-content" style={{ whiteSpace: "pre-wrap", marginBottom: 12 }}>
                      {activeSavedItem.content}
                    </div>
                  ) : null}
                  {activeSavedItem.file_url ? (
                    <p>
                      <a href={activeSavedItem.file_url} target="_blank" rel="noreferrer" className="action-link">
                        Open file / media →
                      </a>
                    </p>
                  ) : null}
                </div>
              ) : (
                <div className="empty-state" style={{ padding: "28px 12px" }}>
                  No item selected.
                </div>
              )
            ) : activeEntry ? (
              <div>
                <p className="help-text" style={{ marginBottom: 8 }}>
                  {titleCase(activeEntry.type_label || "text")} · {formatDate(activeEntry.created_at)}
                  {Number(activeEntry.favourite) === 1 ? " · Favourite" : ""}
                </p>
                {Number(activeEntry.type) === 2 ? (
                  activeEntry.journal_entry ? (
                    <audio controls src={activeEntry.journal_entry} style={{ width: "100%" }}>
                      Your browser does not support the audio element.
                    </audio>
                  ) : (
                    <div className="empty-state" style={{ padding: "28px 12px" }}>
                      Audio file not available.
                    </div>
                  )
                ) : (
                  <div className="journal-content" style={{ whiteSpace: "pre-wrap" }}>
                    {activeEntry.journal_entry || "—"}
                  </div>
                )}
              </div>
            ) : (
              <div className="empty-state" style={{ padding: "28px 12px" }}>
                No entry selected.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

